/**
 * GameControlComponent
 * Combines Whereby video conferencing with game action buttons
 * Features:
 * - First row: Whereby video conferencing section (permission flow → live video)
 * - Second row: Two GlassyActionButton components for game actions
 */

import React from 'react'
import { Box } from '@mui/material'
import { PlayIcon } from '@heroicons/react/24/outline'
import { GlassyActionButton } from '../../../interaxDesignSystemComponents'
import { DashboardCall, DashboardCallFacilitator } from '../../../shared/components/Whereby'
import { INTERAX_COLORS } from '../../../constants/interaxColors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { TESTIDS } from '../../../config/testids'

interface GameControlComponentProps {
  onEnterLobby: () => void
  onStartTutorial: () => void
  className?: string
  mode?: 'gamer' | 'facilitator'
}

/**
 * GameControlComponent - Main game control interface
 * Displays Whereby video conferencing and game action buttons in a glassy container
 */
export const GameControlComponent: React.FC<GameControlComponentProps> = ({
  onEnterLobby,
  className = '',
  mode = 'gamer'
}) => {
  const { t } = useTranslation()
  const isFacilitatorMode = mode === 'facilitator'
  const CallComponent = isFacilitatorMode ? DashboardCallFacilitator : DashboardCall

  return (
    <Box
      className={className}
      sx={{
        // Dark grey container with glassy overlay effect
        backgroundColor: 'rgba(45, 45, 45, 0.85)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        padding: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        maxWidth: '705px',
        maxHeight: '100%',
        width: '100%',
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.4)',
        border: `1px solid ${INTERAX_COLORS.white}22`,
        overflow: 'hidden'
      }}
    >
      {/* First row: Whereby video conferencing */}
      <Box
        sx={{
          width: '100%',
          minHeight: '600px',
          borderRadius: '16px',
          overflow: 'hidden',
          flexShrink: 0
        }}
      >
        <CallComponent width="100%" height="600px" />
      </Box>

      {/* Second row: Action buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          gap: 2,
          alignItems: 'stretch'
        }}
      >
        {/* Enter Team Lobby button - Orange variant */}
        <Box sx={{ flex: 1 }}>
          <GlassyActionButton
            variant="orange"
            onClick={onEnterLobby}
            icon={<PlayIcon style={{ width: '28px', height: '28px' }} />}
            fullWidth
            data-testid={TESTIDS.GAME_CENTER_ENTER_LOBBY_BTN}
          >
            {/* TODO: Add extra logic depending on activity status (is it live, active, etc.) */}
            {t('gamer.gameCenter.enterTeamLobby', 'Enter team lobby')}
          </GlassyActionButton>
        </Box>
      </Box>
    </Box>
  )
}

export default GameControlComponent
